import type { PipLayout } from "../core/types";

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Aspect ratios the PiP box can take. The circle is square. */
export function pipAspect(shape: PipLayout["shape"]): number {
  return shape === "circle" ? 1 : 4 / 3;
}

/** Grid of equal tiles for N screens on a 16:9 canvas. */
export function gridRects(count: number, width: number, height: number): Rect[] {
  if (count <= 0) return [];
  const gap = Math.round(Math.min(width, height) * 0.012);
  let cols = 1;
  let rows = 1;
  if (count === 2) {
    cols = 2;
  } else if (count === 3 || count === 4) {
    cols = 2;
    rows = 2;
  } else if (count > 4) {
    cols = Math.ceil(Math.sqrt(count));
    rows = Math.ceil(count / cols);
  }
  const cellW = Math.floor((width - gap * (cols + 1)) / cols);
  const cellH = Math.floor((height - gap * (rows + 1)) / rows);
  const out: Rect[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    if (row >= rows) break;
    out.push({
      x: gap + col * (cellW + gap),
      y: gap + row * (cellH + gap),
      w: cellW,
      h: cellH,
    });
  }
  return out;
}

/** Aspect-fit inside a tile: the drawn rect, letterboxed and centered. */
export function fitInside(content: { width: number; height: number }, tile: Rect): Rect {
  if (content.width <= 0 || content.height <= 0) return { ...tile, w: 0, h: 0 };
  const scale = Math.min(tile.w / content.width, tile.h / content.height);
  const w = Math.max(1, Math.floor(content.width * scale));
  const h = Math.max(1, Math.floor(content.height * scale));
  return {
    x: tile.x + Math.floor((tile.w - w) / 2),
    y: tile.y + Math.floor((tile.h - h) / 2),
    w,
    h,
  };
}

/** PiP box in canvas pixels, derived from the fractional layout. */
export function pipRect(
  layout: PipLayout,
  width: number,
  height: number,
): Rect {
  const h = Math.max(48, Math.round(height * clamp(layout.size, 0.06, 0.75)));
  const w = Math.round(h * pipAspect(layout.shape));
  const maxX = Math.max(0, width - w);
  const maxY = Math.max(0, height - h);
  // A stored position can be at the corner from a smaller size; keep the box
  // on the canvas so growing it never pushes half the face off the frame.
  return {
    x: Math.min(maxX, Math.round(clamp(layout.x, 0, 1) * width)),
    y: Math.min(maxY, Math.round(clamp(layout.y, 0, 1) * height)),
    w: Math.min(w, width),
    h: Math.min(h, height),
  };
}

/** Move a PiP by a pixel delta, keeping it fully on the canvas. */
export function movePip(
  layout: PipLayout,
  delta: { dx: number; dy: number },
  width: number,
  height: number,
): PipLayout {
  const rect = pipRect(layout, width, height);
  const x = clamp((rect.x + delta.dx) / width, 0, Math.max(0, (width - rect.w) / width));
  const y = clamp((rect.y + delta.dy) / height, 0, Math.max(0, (height - rect.h) / height));
  return { ...layout, x, y };
}

/** Fractional rect of a source frame that gets recorded. */
export interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function clampCrop(crop: CropRect): CropRect {
  const x = clamp(crop.x, 0, 0.98);
  const y = clamp(crop.y, 0, 0.98);
  const w = clamp(crop.w, 0.02, 1 - x);
  const h = clamp(crop.h, 0.02, 1 - y);
  return { x, y, w, h };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
